import React from 'react';
import { Platform, Pressable, StyleSheet, View, ViewStyle } from 'react-native';

import { PencilIcon } from '@/components/atoms/PencilIcon';
import { Typography } from '@/components/atoms/Typography';
import { useTranslation, useUIColors } from '@/core/hooks';

export interface ProfileContactCardProps {
    title: string;
    value: React.ReactNode;
    helperText?: string;
    onEdit?: () => void;
    /**
     * Optional extra style for outer container (used to make side-by-side layout on web).
     */
    containerStyle?: ViewStyle;
}

/**
 * Reusable contact detail card (login email, phone, communication email).
 * Extracted from the mobile PersonalDetailsScreen and reused on web Account & Security.
 */
export function ProfileContactCard({
    title,
    value,
    helperText,
    onEdit,
    containerStyle,
}: ProfileContactCardProps) {
    const colors = useUIColors();
    const { t } = useTranslation();

    return (
        <View style={[styles.card, { backgroundColor: colors.backgroundLevel2 }, containerStyle]}>
            <View style={styles.contentGroup}>
                <Typography variant="subhead" color="primary">
                    {title}
                </Typography>
                {value}
                {helperText ? (
                    <Typography variant="caption" color="tertiary">
                        {helperText}
                    </Typography>
                ) : null}
            </View>
            {onEdit && (
                <Pressable hitSlop={8} style={{ padding: 4, marginLeft: 8 }} onPress={onEdit}>
                    <PencilIcon size={20} color={colors.fontPrimary} />
                </Pressable>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        borderRadius: 12,
        paddingHorizontal: 12,
        paddingVertical: 16,
        gap: 8,
        flexDirection: 'row',
        alignItems: 'center',
    },
    contentGroup: {
        gap: Platform.OS === 'web' ? 8 : 12,
        flex: 1,
        minWidth: 0,
    },
});

export default ProfileContactCard;


